/** Where a filing stands, said one way on every screen (Q-02, E2E gap queue 2026-11-03).
 *
 * **The state is the server's** (`filing_state`, derived on every read and never stored): the
 * headline code and the facts it was chosen from. Home, the Filings list, the filing page and the
 * dashboards each used to work out "is this filing done" from a different subset of the same facts -
 * Home read `issued` as every station ticked while the filing page read `approval.in_force` as no
 * approval - and an auditor could not tell whether version 2 had been approved. Nothing here decides
 * a state; this file only words it, and the three facts are always said when they are true.
 *
 * Pure, so the words are tested without a DOM.
 */

import type { components } from "../api/schema";
import type { ChipTone } from "../components/ui/StatusChip";

export type FilingStateRead = components["schemas"]["FilingStateOut"];
export type FilingStateCode = FilingStateRead["state"];

/** The states in which something has been issued. No deadline is counted down to for any of them:
 * the date belongs to the issue, and an issued filing has nothing left to be late for. */
const ISSUED: ReadonlySet<FilingStateCode> = new Set([
  "issued",
  "issued_figures_moved",
  "issued_reapproval_needed",
  "issued_checks_failing",
]);

export function isIssuedState(state: FilingStateRead): boolean {
  return ISSUED.has(state.state);
}

/** A filing the company is not asked to produce: no stations, no countdown, no chip of progress. */
export function isNotAsked(state: FilingStateRead): boolean {
  return state.state === "not_owed" || state.state === "undetermined";
}

/** Whether a deadline is counted down to. Never for an issued filing (G-P5-10, G-P8-12) and never for
 * one the company is not asked to produce; a regime that is not law yet is the caller's own test. */
export function countsDown(state: FilingStateRead): boolean {
  return !isIssuedState(state) && !isNotAsked(state);
}

/** Issued and something is owed again: the figures moved, the approval on record lapsed, or the checks
 * no longer pass. The plain `issued` state is the only one that is finished. */
export function issuedButOwing(state: FilingStateRead): boolean {
  return isIssuedState(state) && state.state !== "issued";
}

/** Whether the date on a filing still matters to the reader: it is counted down to before anything is
 * issued, and **again once an issued filing owes work** - the figures moved, the approval lapsed or the
 * checks stopped passing (G-REG-02). Home's tile read a bare dash for an issued SB 253 filing that needed
 * approval six days before its date, while the row below it printed that date. Only the plain `issued`
 * state has nothing left to be late for. */
export function dateStillMatters(state: FilingStateRead): boolean {
  return countsDown(state) || issuedButOwing(state);
}

const BASE: Record<FilingStateCode, { word: string; tone: ChipTone }> = {
  not_owed: { word: "Not owed", tone: "neutral" },
  undetermined: { word: "Not determined", tone: "neutral" },
  not_checked: { word: "Not checked", tone: "neutral" },
  unsupported: { word: "Not supported", tone: "neutral" },
  checks_failing: { word: "Incomplete", tone: "warn" },
  ready_for_approval: { word: "Ready for approval", tone: "info" },
  in_approval: { word: "In approval", tone: "info" },
  approved: { word: "Approved", tone: "ok" },
  issued: { word: "Issued", tone: "ok" },
  issued_figures_moved: { word: "Issued", tone: "warn" },
  issued_reapproval_needed: { word: "Issued", tone: "warn" },
  issued_checks_failing: { word: "Issued", tone: "warn" },
};

/** The facts of an issued filing that are true now, in the order of their weight, each a few words.
 * "Issued v2 - figures changed since - re-approval needed" is D-17's option A, said from the facts
 * rather than chosen as a string, so it cannot name a fact the server did not send. It says
 * "re-approval needed" only where the server says an earlier approval existed and was retracted
 * (`approval_lapsed`), and "approval needed" where nobody ever approved it: a filing issued with no
 * approval route behind it has no earlier approval to repeat. */
export function issuedFacts(state: FilingStateRead): string[] {
  if (!isIssuedState(state)) return [];
  const facts: string[] = [];
  if (state.figures_moved === true) facts.push("figures changed since");
  if (state.state === "issued_checks_failing") facts.push("checks not passing now");
  if (!state.approval_in_force) facts.push(state.approval_lapsed === true ? "re-approval needed" : "approval needed");
  return facts;
}

/** The chip's text: the word, then (for an issued filing) its version and the facts that are true
 * now. Joined with a middle dot, which a screen reader reads as a pause.
 *
 * `compact` is for a row that has no room (Home's strip, the dashboards): the version and the single
 * heaviest fact, with the whole sentence as the chip's title. The facts are the same facts; only how
 * many are written changes, so a compact chip can be shorter than the filing page's and never differ
 * from it. */
export function stateWords(
  state: FilingStateRead,
  options: { compact?: boolean } = {},
): { word: string; tone: ChipTone } {
  const base = BASE[state.state];
  if (!isIssuedState(state)) return base;
  const head = state.issued_versions > 1 ? `Issued v${String(state.issued_versions)}` : "Issued";
  const facts = issuedFacts(state);
  return { word: [head, ...(options.compact ? facts.slice(0, 1) : facts)].join(" \u00b7 "), tone: base.tone };
}

/** The whole sentence, for a chip's title or a line of its own. */
export function stateSentence(state: FilingStateRead): string {
  return stateWords(state).word;
}
